import {
  applyProductCardImage,
  getProductCardImages,
} from '@theme/product-card-helpers';
import { getThemeTranslation } from '@theme/utilities';

const PRODUCT_CARD_DRAG_THRESHOLD_PX = 6;
const PRODUCT_CARD_PROGRESS_MIN_THUMB_PX = 8;

const CAROUSEL_SELECTORS = {
  BASE_IMAGE: '[data-product-card-carousel-base-image]',
  TRACK: '[data-product-card-carousel-track]',
  NAV: '[data-product-card-carousel-nav]',
  PROGRESS: '[data-product-card-carousel-progress]',
  PROGRESS_THUMB: '[data-product-card-carousel-progress-thumb]',
  STATUS: '[data-product-card-carousel-status]',
  HELP: '[data-product-card-carousel-help]',
};

let productCardCarouselId = 0;

/**
 * @typedef {import('@theme/product-card-helpers').ProductCardData} ProductCardData
 * @typedef {import('@theme/product-card-helpers').ProductImage} ProductImage
 */

/**
 * @typedef {object} ProductCardCarouselRoot
 * @property {(name: string) => boolean} hasAttribute
 * @property {DOMTokenList} classList
 */

/**
 * @typedef {object} ProductCardCarouselViewModel
 * @property {HTMLElement|null} [$link]
 * @property {HTMLElement|null} [$imageWrapper]
 */

/**
 * @typedef {object} ProductCardImageCarouselOptions
 * @property {ProductCardCarouselRoot} root
 * @property {ProductCardCarouselViewModel} viewModel
 * @property {string|null|undefined} swatchOptionName
 * @property {boolean|null|undefined} isMinimal
 */

/**
 * @param {Element|null|undefined} element
 * @returns {HTMLElement|null}
 */
function asHTMLElement(element) {
  return element instanceof HTMLElement ? element : null;
}

/**
 * @param {Element|null|undefined} element
 * @returns {HTMLImageElement|null}
 */
function asHTMLImageElement(element) {
  return element instanceof HTMLImageElement ? element : null;
}

/**
 * @param {Element} element
 * @returns {element is HTMLButtonElement}
 */
function isHTMLButtonElement(element) {
  return element instanceof HTMLButtonElement;
}

export class ProductCardImageCarousel {
  /** @type {HTMLElement|null} */
  $link = null;

  /** @type {HTMLElement|null} */
  $imageWrapper = null;

  /** @type {HTMLImageElement|null} */
  $baseImage = null;

  /** @type {HTMLElement|null} */
  $track = null;

  /** @type {HTMLElement|null} */
  $progress = null;

  /** @type {HTMLElement|null} */
  $progressThumb = null;

  /** @type {HTMLElement|null} */
  $help = null;

  /** @type {HTMLElement|null} */
  $status = null;

  /** @type {HTMLButtonElement[]} */
  $navButtons = [];

  /** @param {ProductCardImageCarouselOptions} opts */
  constructor({ root, viewModel, swatchOptionName, isMinimal }) {
    this.root = root;
    this.viewModel = viewModel;
    this.swatchOptionName = swatchOptionName;
    this.carouselId = ++productCardCarouselId;
    this.slideCount = 0;
    this.dragState = null;
    this.suppressNextClick = false;
    this.updateFrame = 0;
    this.isMinimal = isMinimal

    this.handleScrollBound = this.scheduleProgressUpdate.bind(this);
    this.handleResizeBound = this.scheduleProgressUpdate.bind(this);
    this.handleClickBound = this.handleClick.bind(this);
    this.handleKeydownBound = this.handleKeydown.bind(this);
    this.handleNavClickBound = this.handleNavClick.bind(this);
    this.handlePointerDownBound = this.handlePointerDown.bind(this);
    this.handlePointerMoveBound = this.handlePointerMove.bind(this);
    this.handlePointerUpBound = this.handlePointerUp.bind(this);
    this.handlePointerLeaveBound = this.handlePointerLeave.bind(this);
    this.handleFocusOutBound = this.handleFocusOut.bind(this);
    this.handleDragStartBound = this.handleDragStart.bind(this);

    this.cacheElements();

    this.$track?.addEventListener('scroll', this.handleScrollBound, {
      passive: true,
    });
    this.$link?.addEventListener('click', this.handleClickBound);
    this.$link?.addEventListener('keydown', this.handleKeydownBound);
    this.$link?.addEventListener('pointerdown', this.handlePointerDownBound);
    this.$link?.addEventListener('dragstart', this.handleDragStartBound);
    this.$imageWrapper?.addEventListener(
      'pointerleave',
      this.handlePointerLeaveBound
    );
    this.$imageWrapper?.addEventListener('focusout', this.handleFocusOutBound);
    window.addEventListener('resize', this.handleResizeBound);
  }

  cacheElements() {
    this.$link = asHTMLElement(this.viewModel.$link);
    this.$imageWrapper = asHTMLElement(this.viewModel.$imageWrapper);
    this.$baseImage = asHTMLImageElement(
      this.$link?.querySelector(CAROUSEL_SELECTORS.BASE_IMAGE)
    );
    this.$track = asHTMLElement(
      this.$link?.querySelector(CAROUSEL_SELECTORS.TRACK)
    );
    this.$progress = asHTMLElement(
      this.$imageWrapper?.querySelector(CAROUSEL_SELECTORS.PROGRESS)
    );
    this.$progressThumb = asHTMLElement(
      this.$progress?.querySelector(CAROUSEL_SELECTORS.PROGRESS_THUMB)
    );
    this.$navButtons = Array.from(
      this.$imageWrapper?.querySelectorAll(CAROUSEL_SELECTORS.NAV) || []
    ).filter(isHTMLButtonElement);
    this.$help = asHTMLElement(
      this.$imageWrapper?.querySelector(CAROUSEL_SELECTORS.HELP)
    );
    this.$status = asHTMLElement(
      this.$imageWrapper?.querySelector(CAROUSEL_SELECTORS.STATUS)
    );

    this.$navButtons.forEach((button) =>
      button.addEventListener('click', this.handleNavClickBound)
    );

    if (this.$help) {
      this.$help.id = `product-card-carousel-help-${this.carouselId}`;
    }

    if (this.$status) {
      this.$status.id = `product-card-carousel-status-${this.carouselId}`;
    }
  }

  /** @param {ProductCardData} data */
  render(data) {
    if (!this.$link || !this.$track || !this.$baseImage) return;

    const images = getProductCardImages(data, this.swatchOptionName);
    const hasCarousel = !this.isMinimal && images.length > 1;
    const fallbackAlt = data.title || '';

    if (!images.length) {
      this.slideCount = 0;
      this.root.classList.remove('product-card-custom--has-image-carousel');
      this.$link.classList.remove('product-card-carousel--multiple');
      this.$link.removeAttribute('aria-describedby');
      if (this.$progress) this.$progress.hidden = true;
      this.$navButtons.forEach((button) => {
        button.hidden = true;
      });
      return;
    }

    const primaryImage = images[0];
    if (!primaryImage) return;

    this.adoptServerLcpImage(this.root.hasAttribute('data-priority-image'));

    this.slideCount = images.length;
    this.$track.replaceChildren(
      ...images.map((image, index) =>
        this.createSlide(image, fallbackAlt, {
          isBase: index === 0,
        })
      )
    );

    applyProductCardImage(this.$baseImage, primaryImage, fallbackAlt);
    this.$baseImage.draggable = false;

    this.root.classList.toggle(
      'product-card-custom--has-image-carousel',
      hasCarousel
    );
    this.$link.classList.toggle('product-card-carousel--multiple', hasCarousel);
    this.$link.setAttribute(
      'aria-label',
      fallbackAlt || getThemeTranslation('product_card_view_product')
    );

    if (this.$progress) this.$progress.hidden = !hasCarousel;
    this.$navButtons.forEach((button) => {
      button.hidden = !hasCarousel;
    });

    if (hasCarousel && this.$help && this.$status) {
      this.$link.setAttribute(
        'aria-describedby',
        `${this.$help.id} ${this.$status.id}`
      );
    } else {
      this.$link.removeAttribute('aria-describedby');
    }

    this.resetScroll();
    this.updateProgress();
  }

  /**
   * Moves the server-rendered PLP LCP image into this card's visible slide.
   * The same element stays in the document, so the request started during HTML parse.
   * @param {boolean} prioritize
   * @returns {HTMLImageElement|null}
   */
  adoptServerLcpImage(prioritize) {
    if (!prioritize) return null;

    const host = this.root instanceof Element ? this.root : null;
    const firstCard = document.querySelector(
      '#algolia-collection-root product-card-custom'
    );
    if (!host || firstCard !== host) return null;

    const lcpImage = document.querySelector('[data-plp-lcp] img');
    if (!(lcpImage instanceof HTMLImageElement)) return null;

    const slideImage = asHTMLImageElement(this.$track?.querySelector('img'));
    if (!slideImage || slideImage === lcpImage) return lcpImage;

    lcpImage.className = slideImage.className;
    lcpImage.draggable = false;
    slideImage.replaceWith(lcpImage);
    document.querySelector('[data-plp-lcp]')?.remove();

    return lcpImage;
  }

  /**
   * @param {string|null|undefined} src
   * @param {string} [alt]
   */
  renderSeedImage(src, alt = '') {
    const seedSrc = String(src || '').trim();
    if (!seedSrc) return;

    const image = { src: seedSrc, alt };
    const prioritize = this.root.hasAttribute('data-priority-image');
    const slideImage =
      this.adoptServerLcpImage(prioritize) ||
      asHTMLImageElement(this.$track?.querySelector('img'));

    if (slideImage) {
      applyProductCardImage(slideImage, image, alt, { prioritize });
    }

    if (this.$baseImage instanceof HTMLImageElement) {
      applyProductCardImage(this.$baseImage, image, alt);
    }
  }

  /**
   * @param {ProductImage} image
   * @param {string} fallbackAlt
   * @param {{ isBase?: boolean }} options
   * @returns {HTMLDivElement}
   */
  createSlide(image, fallbackAlt, options) {
    const isBase = Boolean(options.isBase);
    const prioritize = isBase && this.root.hasAttribute('data-priority-image');

    if (isBase) {
      const existingSlide = this.$track?.querySelector(
        '.product-card-carousel__slide--base'
      );
      const existingImage = asHTMLImageElement(existingSlide?.querySelector('img'));

      if (existingSlide instanceof HTMLDivElement && existingImage) {
        applyProductCardImage(existingImage, image, fallbackAlt, { prioritize });
        existingImage.draggable = false;
        return existingSlide;
      }
    }

    const slide = document.createElement('div');
    const img = document.createElement('img');

    slide.className = 'product-card-carousel__slide';
    slide.classList.toggle('product-card-carousel__slide--base', isBase);
    img.className = 'image-button__image product-card-carousel__image';

    applyProductCardImage(img, image, fallbackAlt, { prioritize });
    img.draggable = false;
    slide.appendChild(img);

    return slide;
  }

  /**
   * @param {number} index
   * @param {ScrollBehavior} [behavior]
   */
  selectSlide(index, behavior = 'smooth') {
    if (!this.$track || this.slideCount <= 1) return;

    const maxIndex = this.getMaxVisibleSlideIndex();
    const nextIndex = Math.max(0, Math.min(maxIndex, index));
    const maxScroll = Math.max(
      0,
      this.$track.scrollWidth - this.$track.clientWidth
    );

    this.$track.scrollTo({
      left: Math.min(maxScroll, nextIndex * this.$track.clientWidth),
      behavior,
    });
  }

  resetScroll() {
    this.selectSlide(0, 'auto');
  }

  scheduleProgressUpdate() {
    if (this.updateFrame) return;

    this.updateFrame = requestAnimationFrame(() => {
      this.updateFrame = 0;
      this.updateProgress();
    });
  }

  updateProgress() {
    const track = this.$track;
    const progress = this.$progress;
    const thumb = this.$progressThumb;

    if (!track || !progress || !thumb) return;

    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    const barWidth = progress.clientWidth;

    if (barWidth > 0 && maxScroll > 0) {
      const rawThumbWidth = (track.clientWidth / track.scrollWidth) * barWidth;
      const thumbWidth = Math.max(
        PRODUCT_CARD_PROGRESS_MIN_THUMB_PX,
        rawThumbWidth
      );
      const maxThumbLeft = Math.max(0, barWidth - thumbWidth);
      const thumbLeft =
        maxThumbLeft <= 0 ? 0 : (track.scrollLeft / maxScroll) * maxThumbLeft;

      thumb.style.width = `${thumbWidth}px`;
      thumb.style.transform = `translateX(${thumbLeft}px)`;
    } else {
      thumb.style.width = '100%';
      thumb.style.transform = 'translateX(0)';
    }

    const currentIndex = this.getCurrentVisibleSlideIndex();
    const hiddenBaseOffset =
      this.slideCount > this.getVisibleSlideCount() ? 1 : 0;
    const currentSlide = Math.min(
      this.slideCount,
      currentIndex + hiddenBaseOffset + 1
    );

    if (this.$status) {
      this.$status.textContent = getThemeTranslation(
        'product_card_image_status',
        {
          index: currentSlide,
          count: Math.max(1, this.slideCount),
        }
      );
    }

    this.updateNavButtons();
  }

  /** @param {MouseEvent} event */
  handleClick(event) {
    if (!this.suppressNextClick) return;

    event.preventDefault();
    event.stopPropagation();
    this.suppressNextClick = false;
  }

  /** @param {KeyboardEvent} event */
  handleKeydown(event) {
    if (!this.$track || this.slideCount <= 1) return;

    switch (event.key) {
      case 'ArrowLeft':
        this.selectSlide(this.getNextVisibleSlideIndex(-1));
        event.preventDefault();
        break;
      case 'ArrowRight':
        this.selectSlide(this.getNextVisibleSlideIndex(1));
        event.preventDefault();
        break;
      case 'Home':
        this.selectSlide(0);
        event.preventDefault();
        break;
      case 'End':
        this.selectSlide(this.getVisibleSlideCount() - 1);
        event.preventDefault();
        break;
      default:
        break;
    }
  }

  /** @param {PointerEvent} event */
  handlePointerDown(event) {
    // needed for carousel safeguard, otherwise the click event will be triggered after dragging
    this.suppressNextClick = false;

    if (!this.$link || !this.$track || this.slideCount <= 1) return;
    if (event.button !== 0) return;

    this.dragState = {
      pointerId: event.pointerId,
      pointerType: event.pointerType,
      startX: event.clientX,
      startY: event.clientY,
      startScrollLeft: this.$track.scrollLeft,
      dragged: false,
    };

    window.addEventListener('pointermove', this.handlePointerMoveBound, {
      passive: false,
    });
    window.addEventListener('pointerup', this.handlePointerUpBound);
    window.addEventListener('pointercancel', this.handlePointerUpBound);
  }

  /** @param {PointerEvent} event */
  handlePointerMove(event) {
    const state = this.dragState;
    if (!state || event.pointerId !== state.pointerId || !this.$track) return;

    const dx = event.clientX - state.startX;
    const dy = event.clientY - state.startY;

    if (
      !state.dragged &&
      Math.abs(dx) > PRODUCT_CARD_DRAG_THRESHOLD_PX &&
      Math.abs(dx) > Math.abs(dy)
    ) {
      state.dragged = true;
      this.$link?.classList.add('product-card-carousel--dragging');
    }

    if (state.dragged && state.pointerType === 'mouse') {
      event.preventDefault();
      this.$track.scrollLeft = state.startScrollLeft - dx;
    }
  }

  /** @param {PointerEvent} event */
  handlePointerUp(event) {
    const state = this.dragState;
    if (!state || event.pointerId !== state.pointerId) return;

    if (state.dragged) this.suppressNextClick = true;

    this.$link?.classList.remove('product-card-carousel--dragging');
    this.dragState = null;

    window.removeEventListener('pointermove', this.handlePointerMoveBound);
    window.removeEventListener('pointerup', this.handlePointerUpBound);
    window.removeEventListener('pointercancel', this.handlePointerUpBound);
  }

  /** @param {MouseEvent} event */
  handleNavClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (!(event.currentTarget instanceof HTMLElement)) return;
    if (event.currentTarget.getAttribute('aria-disabled') === 'true') return;

    const direction =
      event.currentTarget.getAttribute('data-product-card-carousel-nav') ===
      'prev'
        ? -1
        : 1;

    this.selectSlide(this.getNextVisibleSlideIndex(direction));
  }

  /** @param {PointerEvent} event */
  handlePointerLeave(event) {
    if (event.pointerType !== 'mouse' || this.dragState) return;

    if (
      document.activeElement instanceof HTMLElement &&
      this.$imageWrapper?.contains(document.activeElement)
    ) {
      document.activeElement.blur();
    }

    this.resetScroll();
  }

  /** @param {DragEvent} event */
  handleDragStart(event) {
    if (this.slideCount <= 1) return;
    event.preventDefault();
  }

  /** @param {FocusEvent} event */
  handleFocusOut(event) {
    if (
      event.relatedTarget instanceof Node &&
      this.$imageWrapper?.contains(event.relatedTarget)
    ) {
      return;
    }

    this.resetScroll();
  }

  getCurrentVisibleSlideIndex() {
    if (!this.$track || this.$track.clientWidth <= 0) return 0;
    return Math.max(
      0,
      Math.min(
        this.getMaxVisibleSlideIndex(),
        Math.round(this.$track.scrollLeft / this.$track.clientWidth)
      )
    );
  }

  getVisibleSlideCount() {
    return this.getMaxVisibleSlideIndex() + 1;
  }

  getMaxVisibleSlideIndex() {
    if (!this.$track || this.$track.clientWidth <= 0) {
      return Math.max(0, this.slideCount - 1);
    }

    if (this.isMinimal) return 0

    const maxScroll = Math.max(
      0,
      this.$track.scrollWidth - this.$track.clientWidth
    );

    return Math.max(0, Math.round(maxScroll / this.$track.clientWidth));
  }

  /** @param {number} direction */
  getNextVisibleSlideIndex(direction) {
    const maxIndex = this.getMaxVisibleSlideIndex();
    if (maxIndex <= 0) return 0;

    const currentIndex = this.getCurrentVisibleSlideIndex();
    if (direction > 0 && currentIndex >= maxIndex) return 0;
    if (direction < 0 && currentIndex <= 0) return maxIndex;

    return currentIndex + direction;
  }

  updateNavButtons() {
    if (!this.$navButtons.length) return;

    const maxIndex = this.getMaxVisibleSlideIndex();
    const hideNav = maxIndex <= 0;

    this.$navButtons.forEach((button) => {
      button.hidden = hideNav;

      button.disabled = false;
      button.setAttribute('aria-disabled', String(hideNav));
    });
  }

  destroy() {
    if (this.updateFrame) cancelAnimationFrame(this.updateFrame);

    this.$track?.removeEventListener('scroll', this.handleScrollBound);
    this.$link?.removeEventListener('click', this.handleClickBound);
    this.$link?.removeEventListener('keydown', this.handleKeydownBound);
    this.$link?.removeEventListener('pointerdown', this.handlePointerDownBound);
    this.$imageWrapper?.removeEventListener(
      'pointerleave',
      this.handlePointerLeaveBound
    );
    this.$imageWrapper?.removeEventListener('focusout', this.handleFocusOutBound);
    this.$link?.removeEventListener('dragstart', this.handleDragStartBound);
    this.$navButtons.forEach((button) => {
      button.removeEventListener('click', this.handleNavClickBound);
    });
    window.removeEventListener('resize', this.handleResizeBound);
    window.removeEventListener('pointermove', this.handlePointerMoveBound);
    window.removeEventListener('pointerup', this.handlePointerUpBound);
    window.removeEventListener('pointercancel', this.handlePointerUpBound);
  }
}
